/**
 * Shapes of the native Creative Performance page (/creatives), spec
 * docs/superpowers/specs/2026-10-15-native-creative-performance-design.md. Types and the URL vocabularies; nothing
 * else. NULL is "not measurable", never 0, in every number below.
 *
 * @module services/creatives/creativeTypes
 */
import type { AdChannel, CampaignState, EntityStatus } from '@/services/channels/channelTypes';
import type { BoxState, CurrencyChoice, ObjectiveFilter, ResultState } from '@/services/overview/overviewTypes';
import type { ResultMetric } from '@/services/targets/resultMetric';
import { CREATIVE_FORMATS, type CreativeFormat } from './creativeFormat';

/** One get_creative_facts row (migration 123), normalised: one creative and its sums over the period. */
export interface CreativeFact {
  workspaceId: string;
  platform: AdChannel;
  adId: string;
  /** Trimmed; a blank one is null. */
  externalAdId: string | null;
  /** As stored; a blank one reads "(ไม่มีชื่อ) · <id>" on the page. */
  adName: string;
  /** The platform's value; a blank one is null (the name may still give a format). */
  adFormat: string | null;
  adStatus: EntityStatus | null;
  adSetId: string;
  campaignId: string;
  campaignName: string;
  campaignStatus: EntityStatus | null;
  campaignEndDate: string | null;
  /** 'unspecified' when the campaign has none. */
  objective: string;
  accountId: string | null;
  /** Trimmed; null = the account's currency is unknown: the creative is dropped and counted (R14). */
  currency: string | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  videoViews: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  /** The first ⌈n/2⌉ days; null = no row in that half. */
  impressionsFirst: number | null;
  clicksFirst: number | null;
  /** The last ⌊n/2⌋ days; null = no row in that half. */
  impressionsSecond: number | null;
  clicksSecond: number | null;
  /** The Result window's sums (D1's cut); all null when there is no Result window. */
  resultSpend: number | null;
  resultImpressions: number | null;
  resultReach: number | null;
  resultClicks: number | null;
  resultConversions: number | null;
  /** The creative's newest day in the period (R1). */
  lastDate: string;
  /** The ad's first day with impressions, ever; null = none. */
  firstImpressionDate: string | null;
  /** The account's first day in our data; equal to firstImpressionDate = the age is "≥". */
  accountFirstDate: string | null;
}

/** Box 1's sort (spec §4.5); `spend` is the default and is left out of the URL. */
export const CREATIVE_SORTS = ['spend', 'cpr', 'ctr'] as const;
export type CreativeSort = (typeof CREATIVE_SORTS)[number];

/** Box 1's format filter; `all` is the default and is left out of the URL. */
export type FormatFilter = 'all' | CreativeFormat;
export const FORMAT_FILTERS: readonly FormatFilter[] = ['all', ...CREATIVE_FORMATS];

/** Rows drawn before "แสดงทั้งหมด" (spec §3.3). */
export const SHOW_FIRST = 15;

/** R10: whole days, inclusive; atLeast = the creative may be older than our data ("≥ N วัน"). */
export interface CreativeAge {
  days: number;
  atLeast: boolean;
}

/** One table row (spec §3.3, §4.4). */
export interface CreativeRow {
  /** The ad id. */
  key: string;
  workspaceId: string;
  platform: AdChannel;
  /** The ad name as stored ('' = unnamed). */
  name: string;
  externalAdId: string | null;
  format: CreativeFormat | null;
  campaignName: string;
  objective: string;
  /** R11; `unknown` draws no chip. */
  state: CampaignState;
  /** null = "—" (no day with impressions on or before yesterday). */
  age: CreativeAge | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  /** Known impressions under 500 (D1's isLowSample): faded, and never Top or Fatigue. */
  lowSample: boolean;
  /** The metric Result counts; null = no Result. */
  metric: ResultMetric | null;
  result: number | null;
  /** Awareness: per 1,000 (R19). */
  costPerResult: number | null;
  resultState: ResultState;
  /** Reactions + comments + shares; null = every part NULL: the platform sent none ("ไม่ได้รายงาน"). */
  engagement: number | null;
  /** Video views ÷ impressions, video on Facebook or TikTok only; null = "—". */
  viewRate: number | null;
  top: boolean;
  /** The CTR drop when Fatigue (0.26 = 26%); null = no label. */
  fatigueDrop: number | null;
}

/** What the page applies, all on the client (spec §2.2: no read). */
export interface CreativeFilters {
  /** Raw channel ids; [] = every channel. */
  channels: readonly string[];
  objective: ObjectiveFilter;
  /** The requested currency; null = the most spent. */
  currency: string | null;
  format: FormatFilter;
  sort: CreativeSort;
  showAll: boolean;
}

export interface CreativeTableData {
  /** Every row after the filters and the sort: the CSV writes all of them (R15). */
  rows: CreativeRow[];
  /** What the page draws: the first SHOW_FIRST, or every row when showAll. */
  shown: CreativeRow[];
  /** Creatives in the page currency before the channel, objective and format filters: 0 = the period has none (R12). */
  inCurrency: number;
}

export interface CreativeView {
  /** D1's choice over the creatives with a known currency; null until the facts are ready. */
  money: CurrencyChoice | null;
  /** Creatives left out because their account's currency is unknown (R14). */
  unknownCurrency: number;
  /** The newest lastDate among the facts (R1); null = none. */
  latestFactDate: string | null;
  table: BoxState<CreativeTableData>;
}
